import { useEffect, useMemo, useState } from 'react';
import { FieldEditor } from './FieldEditor';
import { ActionEditor } from './ActionEditor';
import { KindIcon } from './icons';
import { actionsOf, actionsOnFlow, flowOf, isAutomation, templatesOnFlow } from '../lib/flowDesign';
import { useT, type MessageKey } from '../lib/i18n';
import {
  CORE_PACKAGE, DIRECTIVE_LISTS, directiveId, listOf, packageOf, packagesOf, rootOf, templateId, usesOf, valuesOf,
  type DirectiveList
} from '../lib/taxonomyGraph';
import {
  FIELD_KEY, labelOf, type FieldDefinition, type FieldType, type LinkDefinition, type RegistryDefinition,
  type TypeDefinition
} from '../lib/registry';

/** Every datatype a field may be given on the taxonomy screen. */
export const ALL_FIELD_TYPES: FieldType[] = ['text', 'integer', 'decimal', 'money', 'quantity', 'boolean', 'date', 'timestamp',
  'choice', 'reference', 'value', 'geometry', 'json', 'file'];

const BOARD_ROLES = ['title', 'state', 'since', 'owner', 'value', 'due', 'customer'];
const OWN_PARTS = ['@state', '@stateSince', '@title'];
const CARDINALITIES = ['one-to-one', 'one-to-many', 'many-to-one', 'many-to-many'];

type Keyed = { key: string; labels?: Record<string, string>; [other: string]: unknown };

/**
 * What is chosen in the graph or the tree, to be edited (docs/en/43 sec.8): a template and its
 * fields, a value type, a link, a board, a package - and any other directive as what it is
 * written as. Every edit is to the working definition; nothing is saved until the draft is.
 */
export function TaxonomyInspector({ definition, published, selected, onChange, onSelect, onOpenDesigner }: {
  definition: RegistryDefinition;
  published: RegistryDefinition | null;
  /** `template:crm.deal`, `value:address`, `boards:board.sales`, `link:worksFor`. */
  selected: string;
  onChange: (next: RegistryDefinition) => void;
  onSelect: (id: string) => void;
  /** A flow opened in its designer, with an action of it chosen. */
  onOpenDesigner?: (flowKey: string, action?: string) => void;
}) {
  const [kind, ...rest] = selected.split(':');
  const key = rest.join(':');
  if (kind === 'flows') return <FlowSummary {...{ definition, onSelect, onOpenDesigner }} flowKey={key} />;
  if (kind === 'actions') {
    const flow = flowOf(definition, actionsOf(definition).find((a) => a.key === key)?.target);
    const isPublished = actionsOf(published).some((a) => a.key === key);
    return (
      <div className="tx-inspector">
        {flow && onOpenDesigner && (
          <button className="tx-open-designer" data-testid="tx-open-in-flow" onClick={() => onOpenDesigner(flow.key, key)}>
            <KindIcon kind="flows" /> <OpenInFlow flow={labelOf(flow.labels, 'en', flow.key)} labels={flow.labels} />
          </button>
        )}
        <ActionEditor definition={definition} actionKey={key} onChange={onChange} published={isPublished}
                      onRemove={isPublished ? undefined : () => {
                        onChange({ ...definition, actions: actionsOf(definition).filter((a) => a.key !== key) } as RegistryDefinition);
                        const target = actionsOf(definition).find((a) => a.key === key)?.target ?? definition.types[0]?.key ?? '';
                        onSelect(flow ? `flows:${flow.key}` : templateId(target));
                      }} />
      </div>
    );
  }
  if (kind === 'template') return <TemplateEditor {...{ definition, published, onChange, onSelect }} typeKey={key} />;
  if (kind === 'value') return <ValueEditor {...{ definition, published, onChange }} valueKey={key} />;
  if (kind === 'link') return <LinkEditor {...{ definition, onChange, onSelect }} linkKey={key} />;
  if (kind === 'boards') return <BoardEditor {...{ definition, onChange, onSelect }} boardKey={key} />;
  if (kind === 'packages') return <PackageEditor {...{ definition, onChange }} packageKey={key} />;
  // Veramenta's screen designer is not ported (GeoNexora draws no registry screens): a screen is edited as written, below.
  if ((DIRECTIVE_LISTS as readonly string[]).includes(kind)) {
    return <DirectiveEditor {...{ definition, onChange }} list={kind as DirectiveList} partKey={key} />;
  }
  return null;
}

// ------------------------------------------------------------------ a flow

function OpenInFlow({ flow, labels }: { flow: string; labels?: Record<string, string> }) {
  const { t, locale } = useT();
  return <>{t('tx.openInFlow', { flow: labelOf(labels, locale, flow) })}</>;
}

/**
 * A flow chosen on the map: what it is in a glance - its states in order, what goes through it,
 * its buttons and automations - and the way into its designer, where it is edited.
 */
function FlowSummary({ definition, flowKey, onSelect, onOpenDesigner }: {
  definition: RegistryDefinition; flowKey: string; onSelect: (id: string) => void;
  onOpenDesigner?: (flowKey: string, action?: string) => void;
}) {
  const { t, locale } = useT();
  const flow = (definition.flows ?? []).find((f) => f.key === flowKey);
  if (!flow) return <div className="empty">{t('taxonomy.gone')}</div>;
  const actions = actionsOnFlow(definition, flowKey);
  const templates = templatesOnFlow(definition, flowKey);
  return (
    <div className="tx-inspector" data-testid="tx-flow-summary">
      <h3>{labelOf(flow.labels, locale, flow.key)} <code>{flow.key}</code></h3>
      <div className="tx-state-strip">
        {flow.states.map((s) => (
          <span key={s.key} className="tx-state-chip" style={{ borderColor: s.colour ?? undefined }}>
            {labelOf(s.labels, locale, s.key)}</span>
        ))}
      </div>
      <p className="hint">{(flow.transitions ?? []).length === 0 ? t('tx.flow.isOpen') : t('tx.sum.moves', { count: (flow.transitions ?? []).length })}
        {' · '}{t('tx.sum.buttons', { count: actions.filter((a) => !isAutomation(a)).length, autos: actions.filter(isAutomation).length })}</p>
      {onOpenDesigner && (
        <button className="primary tx-open-designer-main" data-testid="tx-open-designer" onClick={() => onOpenDesigner(flowKey)}>
          {t('tx.openDesigner')}</button>
      )}
      <h4>{t('tx.flow.goesThrough')}</h4>
      <div className="chips">
        {templates.map((x) => <button key={x.key} className="chip" onClick={() => onSelect(templateId(x.key))}>{labelOf(x.labels, locale, x.key)}</button>)}
        {templates.length === 0 && <span className="hint">{t('tx.flow.noTemplates')}</span>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ helpers

function Labels({ labels, onChange }: { labels?: Record<string, string>; onChange: (next: Record<string, string>) => void }) {
  const { t } = useT();
  return (
    <div className="field-row">
      {(['en', 'tr', 'nl', 'de'] as const).map((language) => (
        <label className="field" key={language}>
          <span>{t('types.labelIn', { language: language.toUpperCase() })}</span>
          <input value={labels?.[language] ?? ''} data-testid={`tx-label-${language}`}
                 onChange={(e) => {
                   const next = { ...(labels ?? {}), [language]: e.target.value };
                   if (!e.target.value) delete next[language];
                   onChange(next);
                 }} />
        </label>
      ))}
    </div>
  );
}

function withList(definition: RegistryDefinition, list: string, items: unknown[]): RegistryDefinition {
  return { ...definition, [list]: items };
}

/** Puts a part in a package, or in the store's own core: it is named by that package's members and by no other. */
function inPackage(definition: RegistryDefinition, partKey: string, pack: string): RegistryDefinition {
  const packages = listOf(definition, 'packages').map((p) => {
    const members = ((p.members as string[] | undefined) ?? []).filter((m) => m !== partKey);
    if (p.key === pack) members.push(partKey);
    return { ...p, members };
  });
  return withList(definition, 'packages', packages);
}

function PackagePicker({ definition, list, partKey, onChange }: {
  definition: RegistryDefinition; list: 'template' | DirectiveList; partKey: string; onChange: (next: RegistryDefinition) => void;
}) {
  const { t } = useT();
  const current = packageOf(definition, list, partKey);
  const named = listOf(definition, 'packages').some((p) => ((p.members as string[] | undefined) ?? []).includes(partKey));
  return (
    <label className="field">
      <span>{t('taxonomy.package')}</span>
      <select value={named ? current : ''} data-testid="tx-package"
              onChange={(e) => onChange(inPackage(definition, partKey, e.target.value))}>
        <option value="">{t('taxonomy.packageInferred', { pack: named ? CORE_PACKAGE : current })}</option>
        {packagesOf(definition).filter((p) => p !== CORE_PACKAGE).map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
    </label>
  );
}

function templateChoices(definition: RegistryDefinition, locale: string) {
  return definition.types.filter((type) => rootOf(definition, type.key) !== 'directive')
    .map((type) => ({ key: type.key, label: `${labelOf(type.labels, locale as never, type.key)} (${type.key})` }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

function valueChoices(definition: RegistryDefinition, locale: string) {
  return valuesOf(definition).map((v) => ({ key: v.key, label: `${labelOf(v.labels, locale as never, v.key)} (${v.key})` }));
}

/** The fields a template has from the ones above it, nearest first. */
function inherited(definition: RegistryDefinition, typeKey: string): { from: string; field: FieldDefinition }[] {
  const types = new Map(definition.types.map((t) => [t.key, t]));
  const out: { from: string; field: FieldDefinition }[] = [];
  const seen = new Set<string>();
  for (let at = types.get(types.get(typeKey)?.parent ?? ''); at && !seen.has(at.key); at = at.parent ? types.get(at.parent) : undefined) {
    seen.add(at.key);
    for (const field of at.fields ?? []) out.push({ from: at.key, field });
  }
  return out;
}

/**
 * A field as one line - its key, what it holds, what it is called, who uses it - opened to be
 * edited. A field just added is open: it has nothing to show folded yet.
 */
function FoldedField({ field, uses, children }: {
  field: FieldDefinition; uses: { where: string; what: string }[];
  children: (uses: { where: string; what: string }[]) => JSX.Element;
}) {
  const { t, locale } = useT();
  const [open, setOpen] = useState(field.key === '');
  if (open) {
    return (
      <div className="tx-field open">
        <button className="ghost tx-fold" onClick={() => setOpen(false)} aria-label={t('taxonomy.fold')}>▾ {field.key}</button>
        {children(uses)}
      </div>
    );
  }
  const what = field.type === 'reference' ? `→ ${field.refersTo ?? '?'}` : field.type === 'value' ? `${field.of ?? '?'}${field.many ? '[]' : ''}` : field.type;
  return (
    <button className="tx-field" data-testid={`tx-field-${field.key}`} onClick={() => setOpen(true)}>
      <code>{field.key}</code>
      <span className={`tx-field-type tx-ft-${field.type}`}>{what}</span>
      <span className="tx-field-label">{labelOf(field.labels, locale, '')}</span>
      {field.required && <span className="chip small">{t('types.required')}</span>}
      {uses.length > 0 && <span className="chip small tx-used" title={uses.map((u) => `${u.where} (${u.what})`).join(', ')}>
        {t('taxonomy.usedCount', { count: uses.length })}</span>}
    </button>
  );
}

// ------------------------------------------------------------------ a template

function TemplateEditor({ definition, published, typeKey, onChange, onSelect }: {
  definition: RegistryDefinition; published: RegistryDefinition | null; typeKey: string;
  onChange: (next: RegistryDefinition) => void; onSelect: (id: string) => void;
}) {
  const { t, locale } = useT();
  const type = definition.types.find((x) => x.key === typeKey);
  const before = published?.types.find((x) => x.key === typeKey);
  const root = rootOf(definition, typeKey);
  const targets = useMemo(() => templateChoices(definition, locale), [definition, locale]);
  const values = useMemo(() => valueChoices(definition, locale), [definition, locale]);
  if (!type) return <div className="empty">{t('taxonomy.gone')}</div>;
  const set = (patch: Partial<TypeDefinition>) =>
    onChange({ ...definition, types: definition.types.map((x) => (x.key === typeKey ? { ...x, ...patch } : x)) });
  const fields = type.fields ?? [];
  const setField = (index: number, next: FieldDefinition) => set({ fields: fields.map((f, at) => (at === index ? next : f)) });
  const below = new Set<string>();
  const collect = (k: string) => definition.types.filter((x) => x.parent === k).forEach((x) => { below.add(x.key); collect(x.key); });
  collect(typeKey);
  const parents = definition.types.filter((x) => x.key !== typeKey && !below.has(x.key) && rootOf(definition, x.key) === root);
  const links = (definition.links ?? []).filter((l) => l.from === typeKey || l.to === typeKey);
  const directives = DIRECTIVE_LISTS.filter((list) => list !== 'links' && list !== 'packages')
    .flatMap((list) => listOf(definition, list).filter((p) => [p.type, p.shows, p.appliesTo, p.target].includes(typeKey))
      .map((p) => ({ list, key: p.key })));
  const binding = type.binding ?? (type.parent ? null : 'dynamic');

  return (
    <div className="tx-inspector" data-testid="tx-template">
      <h3>{labelOf(type.labels, locale, type.key)} <code>{type.key}</code></h3>
      <p className="hint">{t(`taxonomy.lane.${root ?? 'object'}` as MessageKey)}
        {binding === 'native' && <> · {t('taxonomy.native', { table: String(type.table ?? '') })}</>}</p>
      <Labels labels={type.labels} onChange={(labels) => set({ labels })} />
      <div className="field-row">
        <label className="field">
          <span>{t('taxonomy.parent')}</span>
          <select value={type.parent ?? ''} data-testid="tx-parent"
                  onChange={(e) => set({ parent: e.target.value || undefined, root: e.target.value ? undefined : root })}>
            <option value="">{t('taxonomy.noParent')}</option>
            {parents.map((p) => <option key={p.key} value={p.key}>{p.key}</option>)}
          </select>
        </label>
        <label className="field-check">
          <input type="checkbox" checked={type.abstract === true} data-testid="tx-abstract"
                 onChange={(e) => set({ abstract: e.target.checked || undefined })} />
          {t('taxonomy.abstract')}
        </label>
        <PackagePicker definition={definition} list="template" partKey={typeKey} onChange={onChange} />
      </div>

      <h4>{t('taxonomy.fields')}</h4>
      {fields.length === 0 && <p className="hint">{t('taxonomy.noFields')}</p>}
      {fields.map((field, index) => (
        <FoldedField key={index} field={field} uses={field.key ? usesOf(definition, typeKey, field.key) : []}>
          {(uses) => (
            <FieldEditor field={field} kinds={ALL_FIELD_TYPES} targets={targets} valueTypes={values} wording="record"
                         published={(before?.fields ?? []).some((f) => f.key === field.key) && field.key !== ''}
                         uses={uses}
                         onChange={(next) => setField(index, next)}
                         onRemove={() => set({ fields: fields.filter((_, at) => at !== index) })} />
          )}
        </FoldedField>
      ))}
      <button data-testid="tx-add-field" onClick={() => set({ fields: [...fields, { key: '', type: 'text' }] })}>
        {t('types.addField')}
      </button>

      {inherited(definition, typeKey).length > 0 && (
        <>
          <h4>{t('taxonomy.inherited')}</h4>
          <ul className="tx-list">
            {inherited(definition, typeKey).map(({ from, field }) => (
              <li key={`${from}.${field.key}`}><code>{field.key}</code> {field.type}{field.of ? ` (${field.of})` : ''}
                {field.refersTo ? ` → ${field.refersTo}` : ''} <button className="link" onClick={() => onSelect(templateId(from))}>{from}</button></li>
            ))}
          </ul>
        </>
      )}

      <h4>{t('taxonomy.links')}</h4>
      <ul className="tx-list">
        {links.map((l) => (
          <li key={l.key}>
            <button className="link" onClick={() => onSelect(`link:${l.key}`)}>{l.key}</button> {l.from} → {l.to} <span className="hint">{l.cardinality}</span>
          </li>
        ))}
      </ul>
      <NewLink definition={definition} from={typeKey} onChange={onChange} onSelect={onSelect} />

      {directives.length > 0 && (
        <>
          <h4>{t('taxonomy.directivesOf')}</h4>
          <ul className="tx-list">
            {directives.map((d) => (
              <li key={`${d.list}:${d.key}`}>
                <span className="hint">{t(`taxonomy.kind.${d.list}` as MessageKey)}</span>{' '}
                <button className="link" onClick={() => onSelect(directiveId(d.list, d.key))}>{d.key}</button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function NewLink({ definition, from, onChange, onSelect }: {
  definition: RegistryDefinition; from: string; onChange: (next: RegistryDefinition) => void; onSelect: (id: string) => void;
}) {
  const { t, locale } = useT();
  const [key, setKey] = useState('');
  const [to, setTo] = useState('');
  const taken = (definition.links ?? []).some((l) => l.key === key);
  const ok = FIELD_KEY.test(key) && !taken && to !== '';
  return (
    <div className="field-row tx-new-link">
      <label className="field"><span>{t('taxonomy.linkKey')}</span>
        <input value={key} spellCheck={false} data-testid="tx-link-key" onChange={(e) => setKey(e.target.value.trim())} /></label>
      <label className="field"><span>{t('taxonomy.linkTo')}</span>
        <select value={to} data-testid="tx-link-to" onChange={(e) => setTo(e.target.value)}>
          <option value="">{t('taxonomy.choose')}</option>
          {templateChoices(definition, locale).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select></label>
      <button disabled={!ok} data-testid="tx-link-add" onClick={() => {
        const link: LinkDefinition = { key, from, to, cardinality: 'many-to-many' };
        onChange({ ...definition, links: [...(definition.links ?? []), link] });
        setKey('');
        setTo('');
        onSelect(`link:${key}`);
      }}>{t('taxonomy.addLink')}</button>
    </div>
  );
}

// ------------------------------------------------------------------ a value type

function ValueEditor({ definition, published, valueKey, onChange }: {
  definition: RegistryDefinition; published: RegistryDefinition | null; valueKey: string; onChange: (next: RegistryDefinition) => void;
}) {
  const { t, locale } = useT();
  const values = valuesOf(definition);
  const value = values.find((v) => v.key === valueKey);
  const before = valuesOf(published).find((v) => v.key === valueKey);
  const choices = useMemo(() => valueChoices(definition, locale).filter((v) => v.key !== valueKey), [definition, locale, valueKey]);
  if (!value) return <div className="empty">{t('taxonomy.gone')}</div>;
  const set = (patch: Partial<typeof value>) =>
    onChange(withList(definition, 'values', values.map((v) => (v.key === valueKey ? { ...v, ...patch } : v))));
  const fields = value.fields ?? [];
  return (
    <div className="tx-inspector" data-testid="tx-value">
      <h3>{labelOf(value.labels, locale, value.key)} <code>{value.key}</code></h3>
      <p className="hint">{t('taxonomy.valueHint')}</p>
      <Labels labels={value.labels} onChange={(labels) => set({ labels })} />
      <h4>{t('taxonomy.fields')}</h4>
      {fields.map((field, index) => (
        <FoldedField key={index} field={field} uses={[]}>
          {() => (
            <FieldEditor field={field} kinds={ALL_FIELD_TYPES.filter((k) => k !== 'reference')} valueTypes={choices}
                         wording="record" published={(before?.fields ?? []).some((f) => f.key === field.key) && field.key !== ''}
                         onChange={(next) => set({ fields: fields.map((f, at) => (at === index ? next : f)) })}
                         onRemove={() => set({ fields: fields.filter((_, at) => at !== index) })} />
          )}
        </FoldedField>
      ))}
      <button data-testid="tx-add-field" onClick={() => set({ fields: [...fields, { key: '', type: 'text' }] })}>
        {t('types.addField')}
      </button>
    </div>
  );
}

// ------------------------------------------------------------------ a link

function LinkEditor({ definition, linkKey, onChange, onSelect }: {
  definition: RegistryDefinition; linkKey: string; onChange: (next: RegistryDefinition) => void; onSelect: (id: string) => void;
}) {
  const { t, locale } = useT();
  const links = definition.links ?? [];
  const link = links.find((l) => l.key === linkKey);
  if (!link) return <div className="empty">{t('taxonomy.gone')}</div>;
  const set = (patch: Partial<LinkDefinition>) =>
    onChange({ ...definition, links: links.map((l) => (l.key === linkKey ? { ...l, ...patch } : l)) });
  const choices = templateChoices(definition, locale);
  return (
    <div className="tx-inspector" data-testid="tx-link">
      <h3>{labelOf(link.labels, locale, link.key)} <code>{link.key}</code></h3>
      <Labels labels={link.labels} onChange={(labels) => set({ labels })} />
      <div className="field-row">
        <label className="field"><span>{t('taxonomy.linkFrom')}</span>
          <select value={link.from} onChange={(e) => set({ from: e.target.value })}>
            {choices.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></label>
        <label className="field"><span>{t('taxonomy.linkTo')}</span>
          <select value={link.to} onChange={(e) => set({ to: e.target.value })}>
            {choices.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></label>
        <label className="field"><span>{t('taxonomy.cardinality')}</span>
          <select value={link.cardinality ?? 'many-to-many'} onChange={(e) => set({ cardinality: e.target.value })}>
            {CARDINALITIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
        <label className="field"><span>{t('taxonomy.inverse')}</span>
          <input value={link.inverse ?? ''} onChange={(e) => set({ inverse: e.target.value.trim() || undefined })} /></label>
        <label className="field-check">
          <input type="checkbox" checked={link.dated === true} onChange={(e) => set({ dated: e.target.checked || undefined })} />
          {t('taxonomy.dated')}
        </label>
      </div>
      <PackagePicker definition={definition} list="links" partKey={linkKey} onChange={onChange} />
      <p>
        <button className="link" onClick={() => onSelect(templateId(link.from))}>{link.from}</button> →{' '}
        <button className="link" onClick={() => onSelect(templateId(link.to))}>{link.to}</button>
      </p>
      <button className="ghost danger" data-testid="tx-link-remove"
              onClick={() => { onChange({ ...definition, links: links.filter((l) => l.key !== linkKey) }); onSelect(templateId(link.from)); }}>
        {t('taxonomy.removeLink')}
      </button>
    </div>
  );
}

// ------------------------------------------------------------------ a board

function BoardEditor({ definition, boardKey, onChange, onSelect }: {
  definition: RegistryDefinition; boardKey: string; onChange: (next: RegistryDefinition) => void; onSelect: (id: string) => void;
}) {
  const { t, locale } = useT();
  const boards = listOf(definition, 'boards');
  const board = boards.find((b) => b.key === boardKey);
  if (!board) return <div className="empty">{t('taxonomy.gone')}</div>;
  const set = (patch: Partial<Keyed>) => onChange(withList(definition, 'boards', boards.map((b) => (b.key === boardKey ? { ...b, ...patch } : b))));
  const shows = board.shows as string | undefined;
  const fieldsOf = (key: string | undefined): string[] => {
    const types = new Map(definition.types.map((x) => [x.key, x]));
    const out: string[] = [];
    const seen = new Set<string>();
    for (let at = key ? types.get(key) : undefined; at && !seen.has(at.key); at = at.parent ? types.get(at.parent) : undefined) {
      seen.add(at.key);
      (at.fields ?? []).forEach((f) => { if (!out.includes(f.key)) out.push(f.key); });
    }
    return out;
  };
  const roles = (board.roles as Record<string, string> | undefined) ?? {};
  const views = listOf(definition, 'views').filter((v) => v.type === shows || !shows);
  const flows = listOf(definition, 'flows');
  return (
    <div className="tx-inspector" data-testid="tx-board">
      <h3>{labelOf(board.labels, locale, board.key)} <code>{board.key}</code></h3>
      <p className="hint">{t('taxonomy.boardHint')}</p>
      <Labels labels={board.labels} onChange={(labels) => set({ labels })} />
      <div className="field-row">
        <label className="field"><span>{t('taxonomy.boardShows')}</span>
          <select value={shows ?? ''} data-testid="tx-board-shows" onChange={(e) => set({ shows: e.target.value || undefined })}>
            <option value="">{t('taxonomy.choose')}</option>
            {templateChoices(definition, locale).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select></label>
        <label className="field"><span>{t('taxonomy.boardFlow')}</span>
          <select value={(board.flow as string) ?? ''} onChange={(e) => set({ flow: e.target.value || undefined })}>
            <option value="">{t('taxonomy.boardFlowOfType')}</option>
            {flows.map((f) => <option key={f.key} value={f.key}>{f.key}</option>)}
          </select></label>
        <label className="field"><span>{t('taxonomy.boardTile')}</span>
          <select value={(board.tile as string) ?? ''} onChange={(e) => set({ tile: e.target.value || undefined })}>
            <option value="">{t('taxonomy.made')}</option>
            {views.map((v) => <option key={v.key} value={v.key}>{v.key}</option>)}
          </select></label>
        <label className="field"><span>{t('taxonomy.boardOpens')}</span>
          <select value={(board.opens as string) ?? ''} onChange={(e) => set({ opens: e.target.value || undefined })}>
            <option value="">{t('taxonomy.made')}</option>
            {views.map((v) => <option key={v.key} value={v.key}>{v.key}</option>)}
          </select></label>
      </div>
      <h4>{t('taxonomy.boardRoles')}</h4>
      <div className="tx-roles">
        {BOARD_ROLES.map((role) => (
          <label className="field" key={role}><span>{t(`taxonomy.role.${role}` as MessageKey)}</span>
            <select value={roles[role] ?? ''} data-testid={`tx-board-role-${role}`}
                    onChange={(e) => {
                      const next = { ...roles, [role]: e.target.value };
                      if (!e.target.value) delete next[role];
                      set({ roles: next });
                    }}>
              <option value="">—</option>
              {[...OWN_PARTS, ...fieldsOf(shows)].map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
        ))}
      </div>
      <div className="field-row">
        <label className="field"><span>{t('taxonomy.boardTotals')}</span>
          <select value={(board.totals as string) ?? ''} onChange={(e) => set({ totals: e.target.value || undefined })}>
            <option value="">—</option>
            {Object.keys(roles).map((r) => <option key={r} value={r}>{r}</option>)}
          </select></label>
        <PackagePicker definition={definition} list="boards" partKey={boardKey} onChange={onChange} />
      </div>
      {shows && <p><button className="link" onClick={() => onSelect(templateId(shows))}>{shows}</button></p>}
    </div>
  );
}

// ------------------------------------------------------------------ a package

function PackageEditor({ definition, packageKey, onChange }: {
  definition: RegistryDefinition; packageKey: string; onChange: (next: RegistryDefinition) => void;
}) {
  const { t, locale } = useT();
  const packages = listOf(definition, 'packages');
  const pack = packages.find((p) => p.key === packageKey);
  const [filter, setFilter] = useState('');
  if (!pack) return <div className="empty">{t('taxonomy.gone')}</div>;
  const set = (patch: Partial<Keyed>) => onChange(withList(definition, 'packages', packages.map((p) => (p.key === packageKey ? { ...p, ...patch } : p))));
  const members = (pack.members as string[] | undefined) ?? [];
  const requires = (pack.requires as string[] | undefined) ?? [];
  const parts = [
    ...definition.types.map((x) => x.key),
    ...DIRECTIVE_LISTS.filter((l) => l !== 'packages').flatMap((l) => listOf(definition, l).map((p) => p.key))
  ].filter((k, i, all) => all.indexOf(k) === i).filter((k) => !filter || k.includes(filter)).sort();
  return (
    <div className="tx-inspector" data-testid="tx-package">
      <h3>{labelOf(pack.labels, locale, pack.key)} <code>{pack.key}</code></h3>
      <p className="hint">{t('taxonomy.packageHint')}</p>
      <Labels labels={pack.labels} onChange={(labels) => set({ labels })} />
      <div className="field-row">
        <label className="field"><span>{t('taxonomy.packageKind')}</span>
          <select value={(pack.kind as string) ?? 'package'} onChange={(e) => set({ kind: e.target.value })}>
            <option value="package">{t('taxonomy.packageKind.package')}</option>
            <option value="sector">{t('taxonomy.packageKind.sector')}</option>
          </select></label>
        <div className="field"><span>{t('taxonomy.requires')}</span>
          <div className="chips">
            {packages.filter((p) => p.key !== packageKey).map((p) => (
              <button key={p.key} className={`chip${requires.includes(p.key) ? ' on' : ''}`}
                      onClick={() => set({ requires: requires.includes(p.key) ? requires.filter((r) => r !== p.key) : [...requires, p.key] })}>
                {p.key}
              </button>
            ))}
          </div>
        </div>
      </div>
      <h4>{t('taxonomy.members', { count: members.length })}</h4>
      <input placeholder={t('taxonomy.search')} value={filter} onChange={(e) => setFilter(e.target.value)} />
      <ul className="tx-members">
        {parts.map((k) => (
          <li key={k}>
            <label className="field-check">
              <input type="checkbox" checked={members.includes(k)}
                     onChange={(e) => onChange(e.target.checked ? inPackage(definition, k, packageKey)
                       : withList(definition, 'packages', packages.map((p) => (p.key === packageKey ? { ...p, members: members.filter((m) => m !== k) } : p))))} />
              <code>{k}</code>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ------------------------------------------------------------------ any other directive

/**
 * A directive as it is written. A view, a flow, a screen, a binding, a rule set, a mapping, an
 * entry of the menu: each is a document of its own shape, said here as that document, checked
 * as it is typed, and taken into the working definition only when it reads.
 */
function DirectiveEditor({ definition, list, partKey, onChange }: {
  definition: RegistryDefinition; list: DirectiveList; partKey: string; onChange: (next: RegistryDefinition) => void;
}) {
  const { t, locale } = useT();
  const parts = listOf(definition, list);
  const part = parts.find((p) => p.key === partKey);
  const written = useMemo(() => JSON.stringify(part ?? {}, null, 2), [part]);
  const [text, setText] = useState(written);
  const [bad, setBad] = useState<string | null>(null);
  useEffect(() => { setText(written); setBad(null); }, [written]);
  if (!part) return <div className="empty">{t('taxonomy.gone')}</div>;
  function apply(next: string) {
    setText(next);
    try {
      const parsed = JSON.parse(next) as Keyed;
      if (!parsed || typeof parsed !== 'object' || typeof parsed.key !== 'string') {
        setBad(t('taxonomy.jsonNeedsKey'));
        return;
      }
      setBad(null);
      onChange(withList(definition, list, parts.map((p) => (p.key === partKey ? parsed : p))));
    } catch (e) {
      setBad((e as Error).message);
    }
  }
  return (
    <div className="tx-inspector" data-testid="tx-directive">
      <h3>{labelOf(part.labels, locale, part.key)} <code>{part.key}</code></h3>
      <p className="hint">{t(`taxonomy.kind.${list}` as MessageKey)} · {t('taxonomy.directiveHint')}</p>
      <PackagePicker definition={definition} list={list} partKey={partKey} onChange={onChange} />
      <textarea className="tx-json" spellCheck={false} value={text} rows={Math.min(30, written.split('\n').length + 2)}
                data-testid="tx-json" onChange={(e) => apply(e.target.value)} />
      {bad && <p className="hint problem">{bad}</p>}
      <button className="ghost danger" data-testid="tx-directive-remove"
              onClick={() => onChange(withList(definition, list, parts.filter((p) => p.key !== partKey)))}>
        {t('taxonomy.removeDirective')}
      </button>
    </div>
  );
}
